import {useEffect, useRef} from "react";
import {useLocation} from "react-router-dom";
import {RootState, useAppDispatch, useAppSelector} from "../../store";
import i18n from "../../config/i18n";
import {getUserDetails} from "../../store/auth/ActionCreators";
import {getNotifications} from "../../store/notification/ActionCreators";
import {getFeatureToggles, getServerTime} from "../../store/app/ActionCreators";
import SpinTheWheelDialog from "../dialogs/spin-the-wheel-dialog/SpinTheWheelDialog";
import PurchaseConfirmationDialog from "../dialogs/purchase-confirmation-dialog/PurchaseConfirmationDialog";
import BannersWrapper from "../../layout/default/banners-wrapper/BannersWrapper";
import RequiredLogin from "./required-login/RequiredLogin";
import ComingSoon from "./coming-soon/ComingSoon";
import useServerTime from "../../hooks/useServerTime";
import {toggleSpinTheWheel} from "../../store/app/Slice";
import AppRoutes from "./app-routes/AppRoutes";

const App = () => {
    const requiredLogin = useAppSelector((s: RootState) => s.appReducer.featureToggles.login_required);
    const comingSoon = useAppSelector((s: RootState) => s.appReducer.featureToggles.coming_soon);
    const language = useAppSelector((s: RootState) => s.appReducer.language);
    const token = useAppSelector((s: RootState) => s.authReducer.loggedInUser.access_token);
    const isLoggedIn = useAppSelector((s: RootState) => !!s.authReducer.loggedInUser.id);
    const hasSpinToday = useAppSelector((s: RootState) => s.authReducer.loggedInUser.has_spin_today);
    const isAdmin = useAppSelector((s: RootState) => s.authReducer.loggedInUser.is_admin);
    const isClient = useAppSelector((s: RootState) => !!s.authReducer.loggedInUser.client_id);
    const authContextKey = useAppSelector((s: RootState) => {
        const user = s.authReducer.loggedInUser;
        return `${user.id}:${user.is_admin ? "admin" : "user"}:${user.client_id ?? "public"}`;
    });
    const wheelOpen = useAppSelector((s: RootState) => s.appReducer.spinTheWheelOpen);
    const dispatch = useAppDispatch();
    const isFirstRender = useRef(true);
    const isFirstRenderWithUser = useRef(true);
    const interval = useRef<NodeJS.Timeout | null>(null);
    const wheelTimeout = useRef<NodeJS.Timeout | null>(null);
    const { pathname } = useLocation();


    useServerTime();

    // Spin wheel logic
    useEffect(() => {
        if (isLoggedIn && !hasSpinToday && !isAdmin && !isClient && isFirstRenderWithUser.current) {
            wheelTimeout.current = setTimeout(() => dispatch(toggleSpinTheWheel(true)), 10000);
            isFirstRenderWithUser.current = false;
        }
        if (!isLoggedIn) {
            isFirstRenderWithUser.current = true;
        }
        if (!isLoggedIn || isAdmin || isClient) {
            if (wheelTimeout.current) clearTimeout(wheelTimeout.current);
            dispatch(toggleSpinTheWheel(false));
        }
    }, [dispatch, isLoggedIn, hasSpinToday, isAdmin, isClient]);

    useEffect(() => {
        if (wheelOpen && wheelTimeout.current) clearTimeout(wheelTimeout.current);
    }, [wheelOpen]);

    // language
    useEffect(() => {
        i18n.changeLanguage(language);
    }, [language]);

    // get user details on first load
    useEffect(() => {
        if (isLoggedIn && isFirstRender.current) {
            const data = { data: { access_token: token, token_type: "bearer" } };
            dispatch(getUserDetails(data.data));
        }
        isFirstRender.current = false;
    }, [dispatch, isLoggedIn, token]);

    // notifications polling
    useEffect(() => {
        if (isLoggedIn && !isAdmin && !isClient) {
            dispatch(getNotifications());
            interval.current = setInterval(() => {
                dispatch(getNotifications());
            }, 60000);
        } else {
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = null;
            }
        }
    }, [dispatch, isLoggedIn, isAdmin, isClient]);

    // scroll top
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [pathname]);

    // feature toggles
    useEffect(() => {
        dispatch(getFeatureToggles());
    }, [dispatch]);

    // Ensure countdown-dependent UI is refreshed when auth context changes.
    useEffect(() => {
        dispatch(getServerTime());
    }, [dispatch, authContextKey]);

    if (requiredLogin) return <RequiredLogin />;

    const isAdminArea = pathname.startsWith("/admin-panel");

    if (comingSoon && !isAdminArea) return <ComingSoon/>;

    return (
        <>
            <AppRoutes key={authContextKey}/>
            <SpinTheWheelDialog />
            <PurchaseConfirmationDialog/>
            <BannersWrapper/>
        </>
    );
};

export default App;
